<script setup lang="ts">
import { ref, useTemplateRef, onMounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import AccessibleModal from "@SharedVueComponents/Modal/AccessibleModal.vue";
import SvgIcon from "@SharedVueComponents/SvgIcon/SvgIcon.vue";
import SharedButton from "@SharedVueComponents/SharedButton.vue";
import pushDataLayer from "@ocm/services/dataLayer";
import { resourcesHostname, commonResourcePath } from "@ocm/services/ocm-object";

const { t } = useI18n();

const IMAGE_URL = `${resourcesHostname}${commonResourcePath}/images/welcome/2023-herbst-casual-triple-02-web.jpg`;

let closeAction = "Closed";
const trackModal = (action: string = closeAction) => {
	const category = "S_WelcomeLayer";
	pushDataLayer({
		event: "GAevent",
		event_category: category,
		event_action: `${category}${action}`,
		event_label: "",
	});
};

const trackRegistrationCompleted = () => {
	pushDataLayer({
		event: "registrationFunnel",
		funnelStep: "completedRegistration",
	});
};

const dialogEl = useTemplateRef<InstanceType<typeof AccessibleModal>>("dialogEl");
const closeModal = () => {
	closeAction = "Button";
	dialogEl.value?.close();
};

const hasWelcomeModal = ref(false);
onMounted(async () => {
	if (!window.sessionStorage.getItem("registrationCompleted")) return;

	window.sessionStorage.removeItem("registrationCompleted");
	trackRegistrationCompleted();
	hasWelcomeModal.value = true;
	await nextTick();
	dialogEl.value?.open();
	trackModal("Shown");
});
</script>

<template>
	<AccessibleModal
		v-if="hasWelcomeModal"
		ref="dialogEl"
		:headline="t('siteWide.welcomeModal.headline')"
		@closed="trackModal()"
	>
		<div class="ocm-body1 mx-auto max-w-82">
			<img class="mb-6" :src="IMAGE_URL" width="328" height="164" alt="" />

			<h3 class="ocm-headline3 mb-4">{{ t("siteWide.welcomeModal.subline") }}</h3>

			<ul class="mb-10 grid gap-y-2">
				<li class="grid grid-cols-[min-content,1fr] gap-x-3">
					<SvgIcon icon-name="heart-empty" class="size-5" />
					{{ t("siteWide.welcomeModal.benefit1") }}
				</li>
				<li class="grid grid-cols-[min-content,1fr] gap-x-3">
					<SvgIcon icon-name="gutscheine" class="size-5" />
					{{ t("siteWide.welcomeModal.benefit2") }}
				</li>
				<li class="grid grid-cols-[min-content,1fr] gap-x-3">
					<SvgIcon icon-name="club-small" class="size-5" />
					{{ t("siteWide.welcomeModal.benefit3") }}
				</li>
			</ul>

			<SharedButton class="w-full" @click="closeModal">
				{{ t("siteWide.welcomeModal.button") }}
			</SharedButton>
		</div>
	</AccessibleModal>
</template>
